import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Box, Button, Center, Flex, HStack, Heading, Icon, IconButton, Image, Modal, ModalBody, ModalContent,
    ModalFooter, ModalHeader, ModalOverlay, Select, Spinner, Text, Textarea, VStack, Wrap, WrapItem,
    useDisclosure, useToast,
} from '@chakra-ui/react';
import { FiAlertTriangle, FiCheckCircle, FiChevronDown, FiImage, FiMessageSquare, FiSend, FiX } from 'react-icons/fi';
import api, { authAPI, resolveImageUrl } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useChatEnabledStatus } from '../mobile/hooks/useChatEnabled';
import MessageBubble from '../mobile/chat/MessageBubble';

// ─────────────────────────────────────────────────────────────────────────────
// 桌面版「整班群發」。
//
// 【與老師 PWA 的關係】src/mobile/pages/MobileBroadcast.jsx 是同一個功能的手機版。
// 兩邊的規則、文案與送出前的確認關卡刻意保持一致（老師在手機與電腦上會交替使用，
// 行為不一樣就會誤判）。沒有抽成共用元件是因為版面差距太大（單欄 vs 雙欄＋標題列），
// 而共用的部分其實只有「打一支 API」。改動任一邊時請同時看另一邊。
// ─────────────────────────────────────────────────────────────────────────────

// 一次群發最多附幾張圖。不是技術限制，是誤觸的煞車：
// 一張圖＝全班每位家長各收到一則推播，而群發送出後收不回。要放寬改這個數字即可。
const MAX_IMAGES = 9;

function BroadcastDisabledNotice() {
    return (
        <Center h="100%" minH="320px" flexDirection="column" gap={2}>
            <Icon as={FiMessageSquare} boxSize="36px" color="neutral.300" />
            <Text fontSize="sm" color="neutral.500">整班群發尚未啟用</Text>
            <Text fontSize="xs" color="neutral.400">待親師對話功能開啟後即可使用</Text>
        </Center>
    );
}

function BroadcastForm() {
    const toast = useToast();
    const { user } = useAuth();
    const location = useLocation();
    // 從對話頁「群發到這個班」帶過來的班級，少一次選錯班的機會
    const presetClassId = location.state?.classId;
    const { isOpen, onOpen, onClose } = useDisclosure();

    const [classes, setClasses] = useState(null); // null ＝ 尚未載入完成
    const [classId, setClassId] = useState(presetClassId ? String(presetClassId) : '');
    const [text, setText] = useState('');
    // 已經上傳完成的圖片（{id,url}）。選圖當下就上傳，不是按下送出才傳——
    // 群發送出後收不回，不能讓「上傳失敗」這種還救得回來的錯誤，發生在已經送出幾則之後。
    const [images, setImages] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [sending, setSending] = useState(false);
    const fileInputRef = useRef(null);
    // 上一次群發的結果留在畫面上而不是只跳 toast：群發是「送出去就收不回」的動作，
    // 老師需要一個不會自己消失的憑據，確認到底發成功沒有、發給了幾個人。
    const [result, setResult] = useState(null);

    const institutionId = user?.institution_id;

    useEffect(() => {
        let alive = true;
        // 權限不必前端再判一次：/classes/lite 對 teacher 只回自己帶的班、對行政/管理員回機構內全部，
        // 與後端群發端點的把關條件一致，選單裡不會出現送出後才 403 的班。
        // 例外是 super_admin：不帶 institution_id 時 /classes/lite 會回「所有機構」的班，
        // 但群發端點仍以 klass.institution_id == 自己的 institution_id 把關，故必須補這個參數。
        api.get('/classes/lite', { params: institutionId ? { institution_id: institutionId } : {} })
            .then((r) => {
                if (!alive) return;
                setClasses(Array.isArray(r.data) ? r.data : (r.data?.data || []));
            })
            .catch(() => { if (alive) setClasses([]); });
        return () => { alive = false; };
    }, [institutionId]);

    // 依班名排序讓選單順序穩定；後端回傳順序不保證，每次進來位置都變的話很容易選錯班
    const sortedClasses = useMemo(
        () => [...(classes || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'zh-Hant')),
        [classes],
    );

    const selectedClass = sortedClasses.find((c) => String(c.id) === String(classId));
    const trimmed = text.trim();

    // 群發端點一次只收 text 或 image，所以文字＋圖片＝連續打 N 次。
    // 家長因此會收到 N 則訊息、N 則推播——這是 user 拍板的規則（老師發幾則、家長收幾則），
    // 不是要在前端想辦法規避的副作用。
    //
    // 順序刻意是「文字先、圖片後」：老師的文字幾乎都是說明（「明天校外教學要帶這些」），
    // 圖片是它的附件；家長端依到達順序由上往下排，先說明後圖片才讀得順。
    const parts = useMemo(() => {
        const list = [];
        if (trimmed) list.push({ key: 'text', label: '文字訊息', body: { content_type: 'text', text: trimmed } });
        images.forEach((im, i) => list.push({
            key: im.id,
            label: `圖片 ${i + 1}`,
            body: { content_type: 'image', image_url: im.url },
        }));
        return list;
    }, [trimmed, images]);

    // 預覽直接用家長端在用的那顆氣泡元件渲染，不另外刻一個「很像」的：
    // 對話室的呈現之後若有調整（例如「全班」徽章的樣子），預覽會跟著變，不會偷偷走鐘。
    //
    // isMine={false} 是刻意的：老師要確認的是「家長會看到什麼」，
    // 而家長看到的是左側白色氣泡＋發話者名稱＋全班徽章；
    // isMine={true} 畫出來的是老師在自己對話室裡的樣子（右側品牌色），不是家長的視角。
    // created_at 給「現在」而不是留空：家長端氣泡本來就帶時間，留空或顯示 '-' 反而不像實物。
    const previewMessages = useMemo(() => {
        const now = new Date().toISOString();
        return parts.map((p) => ({
            id: `preview-${p.key}`,
            ...p.body,
            is_broadcast: true,
            sender_name: user?.full_name || null,
            created_at: now,
        }));
    }, [parts, user]);

    // 條件是「找得到這個班」而不是「classId 有值」：super_admin 切換機構後班級清單會整份換掉，
    // 舊的 classId（含從對話頁帶過來的）可能已不在清單裡；那時直接擋住，比送了才 403 好。
    // uploading 期間也擋：圖片還沒拿到網址就送出，會漏掉那幾張。
    const canSend = Boolean(selectedClass && parts.length && !uploading && !sending);

    // 改班級或改內容＝開始寫下一則，舊結果就不該再留著——否則「已送出 12 則」會停在
    // 新草稿旁邊，老師會分不清那是這次還是上次的。
    //
    // 但「有未送出項目」的結果卡要留著：老師正要修正後重送，那張卡是他唯一能對照
    //「哪幾則已經出去了」的憑據，群發又收不回，清掉等於逼他用猜的。
    const clearResultIfComplete = () => setResult((r) => (r?.failed ? r : null));

    const changeClass = (v) => { setClassId(v); clearResultIfComplete(); };
    const changeText = (v) => { setText(v); clearResultIfComplete(); };
    const removeImage = (id) => {
        setImages((prev) => prev.filter((im) => im.id !== id));
        clearResultIfComplete();
    };

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files || []);
        e.target.value = ''; // 允許連續選同一張（比照對話室 Composer）
        if (!files.length) return;

        const room = MAX_IMAGES - images.length;
        if (room <= 0) {
            toast({ title: `一次最多附加 ${MAX_IMAGES} 張圖片`, status: 'warning', duration: 3000 });
            return;
        }
        const picked = files.slice(0, room);

        setUploading(true);
        try {
            const formData = new FormData();
            picked.forEach((f) => formData.append('files', f));
            // 沿用對話室老師端傳圖的同一支端點：它的權限依賴是 get_current_active_teacher，
            // 涵蓋範圍剛好等於「可以群發的角色」；產出的網址也在群發端點的信任白名單內。
            const res = await authAPI.communicationBook.uploadPhoto(formData);
            const urls = res.data?.urls || [];
            if (!urls.length) {
                toast({ title: '圖片上傳失敗，請再試一次', status: 'error', duration: 3000 });
                return;
            }
            setImages((prev) => [
                ...prev,
                ...urls.map((url, i) => ({ id: `img-${Date.now()}-${i}`, url })),
            ]);
            clearResultIfComplete();
            if (picked.length < files.length) {
                toast({ title: `超過上限，只加入前 ${picked.length} 張`, status: 'warning', duration: 3000 });
            }
        } catch {
            toast({ title: '圖片上傳失敗，請再試一次', status: 'error', duration: 3000 });
        } finally {
            setUploading(false);
        }
    };

    const doSend = async () => {
        if (!canSend) return;
        setSending(true);

        const sentKeys = [];
        const sentLabels = [];
        let studentCount = 0;
        let pushedTotal = 0;
        let failure = null;

        for (const p of parts) {
            try {
                // 逐則送、不用 Promise.all：後端每則都要逐生寫入＋推播，
                // 並行只會讓失敗時更難講清楚「到底哪幾則出去了」。
                const res = await authAPI.chat.broadcast(classId, p.body);
                sentKeys.push(p.key);
                sentLabels.push(p.label);
                // 每則的 sent 都是同一個班的學生數，取最大值即可（不要相加，那會變成 N 倍人數）
                studentCount = Math.max(studentCount, res.data?.sent ?? 0);
                pushedTotal += res.data?.pushed ?? 0;
            } catch (err) {
                // 停在第一個失敗，不硬著頭皮把剩下的送完：失敗多半是整體性的
                //（旗標關閉／權限／斷線），繼續送只會製造更多失敗；
                // 而且停下來，沒送出的內容才能原封不動留在表單裡等老師重送。
                // 後端的 detail 才講得出真正原因（非帶班班級／旗標關閉／訊息為空）。
                const detail = err?.response?.data?.detail;
                failure = { label: p.label, reason: typeof detail === 'string' ? detail : '連線或伺服器錯誤' };
                break;
            }
        }

        // 只清掉「真的送出去了」的部分，沒送出的原封留著。
        // 這樣老師直接再按一次送出，送的就正好是剛剛沒出去的那幾則，不會重複打擾家長。
        const sentKeySet = new Set(sentKeys);
        if (sentKeySet.has('text')) setText('');
        setImages((prev) => prev.filter((im) => !sentKeySet.has(im.id)));

        setResult({
            className: selectedClass?.name || '',
            sentLabels,
            studentCount,
            pushedTotal,
            failed: failure
                ? { ...failure, restLabels: parts.filter((p) => !sentKeySet.has(p.key)).map((p) => p.label) }
                : null,
        });
        setSending(false);
        onClose();

        if (failure) {
            toast({
                title: `${failure.label}起未送出`,
                description: failure.reason,
                status: 'error',
                duration: 6000,
            });
        } else if (studentCount === 0) {
            // 後端對空班級回 {sent:0}，HTTP 仍是 200。不特別講的話老師會以為發成功了。
            toast({ title: '這個班目前沒有學生，沒有任何人收到', status: 'warning', duration: 4000 });
        }
    };

    if (classes === null) return <Center h="100%" minH="320px"><Spinner /></Center>;

    return (
        <Box maxW="720px">
            <Box mb={4}>
                <Heading size="md" color="neutral.800">整班群發</Heading>
                <Text fontSize="xs" color="neutral.500" mt={0.5}>
                    一次通知全班家長；訊息會分別落在每位學生的對話裡，家長彼此看不到
                </Text>
            </Box>

            {result && (
                <HStack
                    align="flex-start" spacing={2} mb={4} p={3}
                    bg="white" borderRadius="xl" border="1px solid"
                    borderColor={result.failed ? 'red.300' : 'brand.200'}
                >
                    <Icon
                        as={result.failed ? FiAlertTriangle : FiCheckCircle}
                        boxSize="18px" color={result.failed ? 'red.500' : 'brand.500'} mt="2px" flexShrink={0}
                    />
                    <Box>
                        <Text fontSize="sm" fontWeight="600" color="neutral.800">
                            {result.failed
                                ? `「${result.className}」只送出 ${result.sentLabels.length} 則`
                                : `已送出 ${result.sentLabels.length} 則至「${result.className}」`}
                        </Text>
                        <Text fontSize="xs" color="neutral.500" mt={0.5}>
                            {result.sentLabels.length
                                ? `已送出：${result.sentLabels.join('、')}。每則進到 ${result.studentCount} 位學生的對話，累計推播 ${result.pushedTotal} 次`
                                : '沒有任何一則送出去'}
                        </Text>
                        {result.failed && (
                            <Text fontSize="xs" color="red.600" mt={1}>
                                未送出：{result.failed.restLabels.join('、')}（{result.failed.reason}）。
                                未送出的內容仍留在下方，修好後直接再按一次送出即可，已送出的不會重複。
                            </Text>
                        )}
                    </Box>
                </HStack>
            )}

            <VStack
                align="stretch" spacing={4} p={5}
                bg="white" borderRadius="xl" border="1px solid" borderColor="neutral.200"
            >
                <Box>
                    <Text fontSize="sm" fontWeight="600" color="neutral.700" mb={1.5}>群發班級</Text>
                    <Select
                        placeholder={sortedClasses.length ? '請選擇班級' : '查無可群發的班級'}
                        value={classId}
                        onChange={(e) => changeClass(e.target.value)}
                        isDisabled={!sortedClasses.length}
                        borderRadius="lg"
                        bg="neutral.50"
                        icon={<Icon as={FiChevronDown} />}
                        maxW="360px"
                    >
                        {sortedClasses.map((c) => (
                            <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                    </Select>
                </Box>

                <Box>
                    <Text fontSize="sm" fontWeight="600" color="neutral.700" mb={1.5}>訊息內容</Text>
                    <Textarea
                        value={text}
                        onChange={(e) => changeText(e.target.value)}
                        placeholder="輸入要發給全班家長的訊息…"
                        rows={8}
                        resize="vertical"
                        bg="neutral.50"
                        borderRadius="lg"
                        fontSize="sm"
                        // 這裡不套對話室 Composer 的 Enter 送出：群發按下去就收不回，
                        // 打字時誤觸 Enter 的代價太高，一律要走下方按鈕 → 確認視窗。
                    />
                </Box>

                <Box>
                    <HStack justify="space-between" mb={1.5}>
                        <Text fontSize="sm" fontWeight="600" color="neutral.700">附加圖片（可不填）</Text>
                        <Text fontSize="xs" color="neutral.400">{images.length}/{MAX_IMAGES}</Text>
                    </HStack>
                    <Wrap spacing={2}>
                        {images.map((im) => (
                            <WrapItem key={im.id}>
                                <Box position="relative">
                                    <Image
                                        src={resolveImageUrl(im.url)}
                                        alt=""
                                        boxSize="72px"
                                        objectFit="cover"
                                        borderRadius="lg"
                                        border="1px solid"
                                        borderColor="neutral.200"
                                    />
                                    <IconButton
                                        aria-label="移除這張圖片"
                                        icon={<FiX />}
                                        size="xs"
                                        borderRadius="full"
                                        colorScheme="blackAlpha"
                                        position="absolute"
                                        top="-6px"
                                        right="-6px"
                                        minW="20px"
                                        h="20px"
                                        isDisabled={sending}
                                        onClick={() => removeImage(im.id)}
                                    />
                                </Box>
                            </WrapItem>
                        ))}
                        <WrapItem>
                            <Button
                                variant="outline"
                                h="72px"
                                w="72px"
                                borderRadius="lg"
                                borderStyle="dashed"
                                color="neutral.600"
                                isLoading={uploading}
                                isDisabled={images.length >= MAX_IMAGES || sending}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <Icon as={FiImage} boxSize="20px" />
                            </Button>
                        </WrapItem>
                    </Wrap>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        hidden
                        onChange={handleFileChange}
                    />
                </Box>

                <HStack align="flex-start" spacing={2}>
                    <Icon as={FiAlertTriangle} boxSize="14px" color="neutral.500" mt="3px" flexShrink={0} />
                    <Text fontSize="xs" color="neutral.500">
                        文字與每張圖片各算一則，分別進到全班每位學生的對話（家長端顯示「全班」徽章）；
                        送出幾則，家長就收到幾則推播，送出後無法收回。
                    </Text>
                </HStack>

                <Flex justify="flex-end">
                    <Button
                        colorScheme="brand"
                        leftIcon={<Icon as={FiSend} />}
                        isDisabled={!canSend}
                        onClick={onOpen}
                    >
                        預覽並送出{parts.length > 1 ? `（${parts.length} 則）` : ''}
                    </Button>
                </Flex>
            </VStack>

            <Text fontSize="xs" color="neutral.400" mt={3}>
                送出前會先預覽家長看到的樣子。
            </Text>

            {/* 送出前的確認關卡：一次發給整班家長且會發推播，誤觸代價高，
                所以確認視窗要把「發給哪一班」與「發出去的完整內容」都攤開讓老師再看一次。 */}
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                isCentered
                closeOnOverlayClick={!sending}
                closeOnEsc={!sending}
            >
                <ModalOverlay />
                <ModalContent mx={4}>
                    <ModalHeader fontSize="md">確認群發</ModalHeader>
                    <ModalBody pt={0}>
                        <Text fontSize="sm" color="neutral.700">
                            將發送給
                            <Text as="span" fontWeight="bold" color="neutral.900"> {selectedClass?.name || ''} </Text>
                            全班學生的家長，共
                            <Text as="span" fontWeight="bold" color="neutral.900"> {parts.length} 則</Text>
                            （每位家長會收到 {parts.length} 則推播），送出後無法收回。
                        </Text>
                        {/* 預覽底色刻意用 App 畫布色：家長就是在這個底色上看訊息。
                            用白底預覽的話白色氣泡會整個融掉，看到的跟實物不一樣。 */}
                        <Box
                            mt={3} p={3} maxH="260px" overflowY="auto"
                            bg="canvas" borderRadius="lg"
                            sx={{ overscrollBehavior: 'contain' }}
                        >
                            {previewMessages.map((m) => (
                                <MessageBubble key={m.id} message={m} isMine={false} />
                            ))}
                        </Box>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={2} onClick={onClose} isDisabled={sending}>取消</Button>
                        <Button colorScheme="brand" leftIcon={<Icon as={FiSend} />} isLoading={sending} onClick={doSend}>
                            確認送出
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
}

export default function TeacherBroadcastPage() {
    const { enabled, loading } = useChatEnabledStatus();
    // 旗標查完前先 Spinner，不要先閃一次「尚未啟用」
    if (loading) return <Center h="100%" minH="320px"><Spinner /></Center>;
    return enabled ? <BroadcastForm /> : <BroadcastDisabledNotice />;
}
